import { useEffect, useState } from "react";
import { RefreshCw, ChevronDown, ChevronRight, Play } from "lucide-react";
import { api } from "../lib/api";

const KIND_LABEL = {
    trainer: "Trainer footage",
    cinematic: "Cinematic shots",
    voice: "Voice narration",
    stills: "Scene stills",
};

/**
 * Live "Generating your video" card. Polls /modules/:id/build-progress
 * every 4s until the module reports `state === "ready"` or the caller
 * unmounts. Also surfaces an admin-only "See details" expander when
 * the pipeline reports a hard failure.
 */
export default function BuildProgressCard({ moduleId, isAdmin, onReady, renderGated = false }) {
    const [progress, setProgress] = useState(null);
    const [showAdminDetails, setShowAdminDetails] = useState(false);

    useEffect(() => {
        if (!moduleId) return undefined;
        let alive = true;
        let timer = null;
        const tick = async () => {
            try {
                const { data } = await api.get(`/modules/${moduleId}/build-progress`);
                if (!alive) return;
                setProgress(data);
                if (data.state === "ready") {
                    onReady?.(data);
                }
            } catch {
                // Silent — polling will just retry on the next tick.
            } finally {
                if (alive) timer = setTimeout(tick, 4000);
            }
        };
        tick();
        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [moduleId, onReady]);

    if (!progress) {
        return (
            <div
                data-testid="build-progress-loading"
                className="rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-500 flex items-center gap-2"
            >
                <RefreshCw size={13} className="animate-spin" /> Checking your video…
            </div>
        );
    }

    const cooking = Object.entries(progress.assets || {})
        .filter(([, v]) => (v.total || 0) > (v.ready || 0))
        .map(([k]) => KIND_LABEL[k])
        .filter(Boolean);

    const isReady = progress.state === "ready";
    const isAssembling = progress.state === "assembling";
    const isHardFailed = progress.state === "hard_failed";
    const isAwaitingApproval = progress.state === "awaiting_approval";

    // Pre-approval short-circuit — do not render the "Generating your
    // video" ceremony for a module the reviewer hasn't approved.
    if (isAwaitingApproval) {
        return (
            <div
                data-testid="build-progress-awaiting-approval"
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex flex-col gap-2"
            >
                <div className="font-semibold text-base">Storyboard needs approval</div>
                <div className="text-xs">
                    {progress.current_step_message ||
                        "Approve every scene before we build the video."}
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="build-progress-card"
            className="rounded-lg border border-neutral-200 bg-white overflow-hidden"
        >
            <div className="border-b border-neutral-200 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-neutral-500 flex items-center justify-between">
                <span>Video build progress</span>
                <span
                    data-testid="build-progress-percent-pill"
                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${
                        isReady ? "bg-emerald-50 text-emerald-700"
                            : isHardFailed ? "bg-amber-50 text-amber-800"
                                : "bg-blue-50 text-blue-700"
                    }`}
                >
                    {progress.percent}%
                </span>
            </div>
            <div className="p-4 space-y-4">
                <div>
                    <div className="text-lg font-medium text-neutral-900" data-testid="build-progress-heading">
                        {isReady ? "Ready to build your video"
                            : isAssembling ? "Assembling your video"
                                : "Generating your video"}
                    </div>
                    <div className="mt-0.5 text-xs text-neutral-500" data-testid="build-progress-eta">
                        {isReady ? "All assets are ready — one click to finish."
                            : progress.eta_display}
                    </div>
                </div>

                {/* Progress bar */}
                <div className="h-2 rounded-full bg-neutral-100 overflow-hidden" data-testid="build-progress-bar">
                    <div
                        className={`h-full transition-all duration-700 ${
                            isReady ? "bg-emerald-500" : "bg-blue-500"
                        }`}
                        style={{ width: `${Math.max(4, progress.percent)}%` }}
                    />
                </div>

                {/* Stalled-slot detector — surfaces the durable poller's
                    "Retrying trainer video" warnings so the progress bar
                    never looks silently frozen. */}
                {!isReady && (progress.slot_warnings || []).length > 0 && (
                    <div
                        data-testid="build-progress-slot-warnings"
                        className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1"
                    >
                        {progress.slot_warnings.map((w, i) => (
                            <div
                                key={`${w.slot_id}-${i}`}
                                data-testid={`build-progress-slot-warning-${w.slot_id}`}
                                className="flex items-start gap-2"
                            >
                                <RefreshCw size={12} className="mt-0.5 shrink-0 animate-spin" />
                                <span>{w.message || (
                                    w.kind === "ai_visual_regenerating"
                                        ? "Generating visuals"
                                        : w.kind === "motion_render_retrying"
                                            ? "Retrying scene render"
                                            : "Retrying trainer video"
                                )}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Phase 5.4 — long-running trainer render banner. When a
                    trainer scene has been queued/processing > 3 min we
                    surface a plain-English hint so the admin can swap
                    providers without digging into logs. Vendor-neutral
                    copy — never names HeyGen or the alternative. */}
                {!isReady && (progress.long_running_trainer_scenes || []).length > 0 && (
                    <div
                        data-testid="build-progress-trainer-latency-banner"
                        className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 space-y-1"
                    >
                        <div className="font-medium">
                            Trainer render is taking longer than usual
                        </div>
                        <div>
                            {progress.long_running_trainer_scenes.length === 1
                                ? "A trainer scene has been in the queue for over 3 minutes."
                                : `${progress.long_running_trainer_scenes.length} trainer scenes have been queued for over 3 minutes.`}
                            {isAdmin && " Admins can switch the trainer provider in Advanced \u2192 Build Options."}
                        </div>
                    </div>
                )}

                {/* Current step message */}
                {!isReady && (
                    <div
                        data-testid="build-progress-current-step"
                        className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-900 flex items-start gap-2"
                    >
                        <RefreshCw size={13} className="mt-0.5 shrink-0 animate-spin" />
                        <span>{progress.current_step_message}</span>
                    </div>
                )}

                {/* Dot-list of what's still cooking */}
                {!isReady && cooking.length > 0 && (
                    <div
                        data-testid="build-progress-cooking-list"
                        className="text-[11px] text-neutral-500 flex flex-wrap gap-x-3 gap-y-1"
                    >
                        <span className="uppercase tracking-widest">Still cooking:</span>
                        {cooking.map((c, i) => (
                            <span key={c} className="inline-flex items-center gap-1">
                                <span className="h-1 w-1 rounded-full bg-neutral-400" />
                                {c}
                                {i < cooking.length - 1 && <span className="ml-1 text-neutral-300">·</span>}
                            </span>
                        ))}
                    </div>
                )}

                {/* Scenes ready summary */}
                <div className="text-[11px] text-neutral-500 flex items-center justify-between mono">
                    <span>Scenes ready</span>
                    <span data-testid="build-progress-scenes-ready">
                        {progress.scenes_ready} / {progress.scenes_total}
                    </span>
                </div>

                {/* Ready → Build button (gated by ``renderGated``) */}
                {isReady && !renderGated && (
                    <button
                        type="button"
                        data-testid="build-progress-build-btn"
                        onClick={() => onReady?.(progress, true)}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-neutral-900 text-white px-3 py-2 text-sm font-medium hover:bg-neutral-800"
                    >
                        <Play size={13} /> Build video
                    </button>
                )}
                {isReady && renderGated && (
                    <div
                        data-testid="build-progress-gated-notice"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                    >
                        Review &amp; approve the storyboard before generating the video.
                    </div>
                )}

                {/* Admin-only "See details" for hard failures */}
                {isHardFailed && isAdmin && (progress.hard_failures || []).length > 0 && (
                    <div
                        data-testid="build-progress-admin-expander"
                        className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
                    >
                        <button
                            type="button"
                            data-testid="build-progress-see-details-btn"
                            onClick={() => setShowAdminDetails((v) => !v)}
                            className="inline-flex items-center gap-1 font-medium hover:underline"
                        >
                            {showAdminDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            See details
                        </button>
                        {showAdminDetails && (
                            <ul
                                data-testid="build-progress-admin-messages"
                                className="mt-2 space-y-1 list-disc pl-4"
                            >
                                {progress.hard_failures.map((f, i) => (
                                    <li key={`${f.kind}-${i}`}>{f.admin_message}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {/* Soft layman line for hard-failed state, non-admin */}
                {isHardFailed && !isAdmin && (
                    <div
                        data-testid="build-progress-soft-error"
                        className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
                    >
                        A small manual step is needed. Please contact your admin.
                    </div>
                )}
            </div>
        </div>
    );
}
